import { type Moment, moment } from "../date";
import type { AttendanceList, AttendanceListItem, AutomaticBreak } from "../types";
import { getAttendancesSeconds } from "./get-attendances-seconds";
import { getMissingBreakSeconds, WORK_SECONDS_BEFORE_BREAK } from "./legal-break";

type WorkEntry = AttendanceListItem & { endTime: string };

/**
 * The break a check-out adds when the day's rest falls short of ArbZG §4: how long it is, and
 * which work entry gives the time up.
 *
 * The length is `getMissingBreakSeconds`: the shortfall, or — for a day only a little past six
 * or nine hours, within the organisation's buffer — just what cuts it back to the threshold. Rest
 * is the time between the day's work, whether a break entry records it or not: a check-in records
 * the gap it closes, but an entry added after the fact leaves its gaps unrecorded, and the person
 * was resting there all the same. Where the break goes follows the act, which asks for the work to
 * be interrupted and for no stretch of more than six hours without rest:
 *
 * 1. At the six-hour mark of the first stretch of uninterrupted work longer than six hours,
 *    splitting the entry that holds the mark.
 * 2. Without such a stretch the person already broke the day up, so the break is added onto the
 *    longest rest they took, carved off the start of the entry that follows it. That makes one
 *    block, which also meets the act's 15-minute minimum.
 * 3. Failing both, off the end of the latest entry that ends its stretch.
 *
 * Each placement leaves a different trace — a split has work on both sides of the break, an `end`
 * carve work only before it, a `start` carve work only after it — because a carve at an entry's
 * end or start is made only where no other work entry touches that end or start. That trace is
 * what lets `getAutomaticBreakRestores` take the break back from the rows alone.
 *
 * @param dayAttendances - Every entry of the person's day, the one just checked out of included.
 * @param bufferSeconds - How far past a threshold a day is still cut back to it, the
 *   organisation's `automaticBreakBufferMinutes`.
 * @returns The break to carve, or `null` when the day's rest is enough or no entry can hold it.
 */
export function getAutomaticBreak(
	dayAttendances: AttendanceList,
	bufferSeconds: number,
): AutomaticBreak | null {
	const stretches = getWorkStretches(dayAttendances);
	const missingSeconds = getMissingBreakSeconds(
		getAttendancesSeconds(dayAttendances, "work"),
		getRestSeconds(stretches),
		bufferSeconds,
	);
	if (missingSeconds <= 0) return null;

	return (
		placeAtSixHourMark(stretches, missingSeconds) ??
		placeAfterLongestRest(stretches, missingSeconds) ??
		placeAtEndOfStretch(stretches, missingSeconds)
	);
}

function isClosedWork(attendance: AttendanceListItem): attendance is WorkEntry {
	return attendance.type === "work" && attendance.state !== "rejected" && !!attendance.endTime;
}

function getDurationSeconds(startTime: string, endTime: string) {
	return moment(endTime).diff(moment(startTime), "seconds");
}

/**
 * The day's closed, credited work entries in order, grouped into stretches: runs of entries that
 * follow each other without a gap — a worksite change ends one entry where the next begins — and
 * so without rest between them.
 */
function getWorkStretches(dayAttendances: AttendanceList): WorkEntry[][] {
	const entries: WorkEntry[] = [];
	for (const attendance of dayAttendances) {
		if (isClosedWork(attendance)) entries.push(attendance);
	}
	entries.sort((a, b) => moment(a.startTime).diff(moment(b.startTime)));

	const stretches: WorkEntry[][] = [];
	for (const entry of entries) {
		const stretch = stretches[stretches.length - 1];
		const previous = stretch?.[stretch.length - 1];
		if (stretch && previous && moment(entry.startTime).isSame(previous.endTime)) {
			stretch.push(entry);
		} else {
			stretches.push([entry]);
		}
	}
	return stretches;
}

function toAutomaticBreak(
	entry: WorkEntry,
	start: Moment,
	end: Moment,
	carve: AutomaticBreak["carve"],
): AutomaticBreak {
	return {
		attendanceId: entry.id,
		startTime: start.toISOString(),
		endTime: end.toISOString(),
		carve,
	};
}

/**
 * The break at the six-hour mark of the first stretch longer than six hours, splitting the entry
 * that holds the mark — only when the break fits strictly inside it, since a split is the one
 * carve that may sit beside another work entry.
 */
function placeAtSixHourMark(stretches: WorkEntry[][], seconds: number): AutomaticBreak | null {
	for (const stretch of stretches) {
		const first = stretch[0];
		const last = stretch[stretch.length - 1];
		if (!first || !last) continue;
		const stretchStart = moment(first.startTime);
		if (moment(last.endTime).diff(stretchStart, "seconds") <= WORK_SECONDS_BEFORE_BREAK) continue;

		const start = stretchStart.clone().add(WORK_SECONDS_BEFORE_BREAK, "seconds");
		const end = start.clone().add(seconds, "seconds");
		for (const entry of stretch) {
			if (start.isAfter(entry.startTime) && end.isBefore(entry.endTime)) {
				return toAutomaticBreak(entry, start, end, "split");
			}
		}
		return null;
	}
	return null;
}

/** The rest between the day's stretches of work: every gap between one and the next. */
function getRestSeconds(stretches: WorkEntry[][]): number {
	let seconds = 0;
	for (let index = 1; index < stretches.length; index++) {
		const previous = stretches[index - 1];
		const last = previous?.[previous.length - 1];
		const first = stretches[index]?.[0];
		if (last && first) seconds += getDurationSeconds(last.endTime, first.startTime);
	}
	return seconds;
}

/**
 * The break added onto the longest rest the person took — the latest of equals — carved off the
 * start of the entry that ends it. That entry opens its stretch, so no other work entry ends
 * where the carve begins.
 */
function placeAfterLongestRest(stretches: WorkEntry[][], seconds: number): AutomaticBreak | null {
	let placed: AutomaticBreak | null = null;
	let placedRestSeconds = -1;
	for (let index = 1; index < stretches.length; index++) {
		const previous = stretches[index - 1];
		const last = previous?.[previous.length - 1];
		const first = stretches[index]?.[0];
		if (!last || !first) continue;
		if (getDurationSeconds(first.startTime, first.endTime) <= seconds) continue;
		const restSeconds = getDurationSeconds(last.endTime, first.startTime);
		if (restSeconds < placedRestSeconds) continue;

		const start = moment(first.startTime);
		placed = toAutomaticBreak(first, start, start.clone().add(seconds, "seconds"), "start");
		placedRestSeconds = restSeconds;
	}
	return placed;
}

/**
 * The break off the end of the latest entry that ends its stretch and is longer than the break.
 * Ending its stretch, no other work entry begins where the carve ends.
 */
function placeAtEndOfStretch(stretches: WorkEntry[][], seconds: number): AutomaticBreak | null {
	let latest: WorkEntry | null = null;
	for (const stretch of stretches) {
		const last = stretch[stretch.length - 1];
		if (!last || getDurationSeconds(last.startTime, last.endTime) <= seconds) continue;
		if (latest && !moment(last.endTime).isAfter(latest.endTime)) continue;
		latest = last;
	}
	if (!latest) return null;
	const end = moment(latest.endTime);
	return toAutomaticBreak(latest, end.clone().subtract(seconds, "seconds"), end, "end");
}
